import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { IconCheck, IconMinus } from '@tabler/icons-react';
import { Label } from '../Label';
import { cn } from '../../lib/utils';
import type { CheckboxProps } from './types';

const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>((props, ref) => {
  const {
    className,
    label,
    id,
    required,
    checked,
    error = false,
    'aria-invalid': ariaInvalidProp,
    ...restProps
  } = props;
  const Icon = checked === 'indeterminate' ? IconMinus : IconCheck;
  const generatedId = React.useId();
  const checkboxId = id ?? generatedId;
  const labelId = label ? `${checkboxId}-label` : undefined;
  const hasAriaLabel = 'aria-label' in restProps;

  return (
    <span className='eye:flex eye:items-center eye:gap-2xs'>
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        checked={checked}
        required={required}
        aria-invalid={ariaInvalidProp ?? (error || undefined)}
        aria-labelledby={hasAriaLabel ? undefined : labelId}
        className={cn(
          'eye:focus-visible:ring-black eye:flex eye:cursor-pointer eye:items-center eye:justify-center eye:rounded-sm eye:border eye:border-grey-100 eye:bg-white eye:transition-colors eye:focus-visible:ring-2 eye:focus-visible:outline-none eye:disabled:cursor-not-allowed eye:disabled:opacity-50',
          error && 'eye:border-red-100 eye:hover:border-red-100',
          'eye:size-20',
          className,
        )}
        {...restProps}
      >
        <CheckboxPrimitive.Indicator className='eye:flex eye:items-center eye:justify-center'>
          <Icon stroke={3} className='eye:size-16' aria-hidden='true' />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>

      {label && (
        <Label
          id={labelId}
          htmlFor={checkboxId}
          className={cn(
            'eye:body-m eye:cursor-pointer',
            error && 'eye:text-red-100',
            restProps.disabled &&
              'eye:cursor-not-allowed eye:text-grey-disabled',
          )}
        >
          {label}
          {required && <span aria-hidden='true'>{' *'}</span>}
        </Label>
      )}
    </span>
  );
});

export { Checkbox };
